/**
 * @fileoverview Line chart of a figure per month.
 * @description A small dependency-free SVG line chart for the Sales Performance screen's two
 * monthly series (targets achieved and banks onboarded): three grid lines, one dot per month with
 * the value in its tooltip, and the month names along the bottom. Takes its colour from the text
 * colour of the class it is given, so the same chart serves both series.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

const WIDTH = 520
const HEIGHT = 190
const PAD = { left: 34, right: 14, top: 14, bottom: 28 }
const GRID_STEPS = [0, 0.5, 1]
const TOP_ROUNDING = 5

/** @description One month of the series. */
export interface MonthPoint {
  label: string
  value: number
}

interface PerformanceMonthChartProps {
  points: MonthPoint[]
  colorClass: string
  ariaLabel: string
  testId: string
}

/**
 * @component
 * @param {PerformanceMonthChartProps} props - The series and how to draw it.
 * @param {MonthPoint[]} props.points - One point per month, earliest first.
 * @param {string} props.colorClass - A Tailwind text-colour class; the line takes its colour.
 * @param {string} props.ariaLabel - The chart's accessible name.
 * @param {string} props.testId - `data-testid` of the chart.
 */
function PerformanceMonthChart({
  points,
  colorClass,
  ariaLabel,
  testId,
}: PerformanceMonthChartProps) {
  const max = Math.max(1, ...points.map((point) => point.value))
  const top = Math.ceil(max / TOP_ROUNDING) * TOP_ROUNDING
  const x = (index: number) =>
    PAD.left + (index * (WIDTH - PAD.left - PAD.right)) / Math.max(1, points.length - 1)
  const y = (value: number) => PAD.top + (1 - value / top) * (HEIGHT - PAD.top - PAD.bottom)

  return (
    <svg
      viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
      width="100%"
      role="img"
      aria-label={ariaLabel}
      data-testid={testId}
      className={colorClass}
    >
      {GRID_STEPS.map((step) => {
        const value = Math.round(top * step)
        return (
          <g key={step}>
            <line
              x1={PAD.left}
              x2={WIDTH - PAD.right}
              y1={y(value)}
              y2={y(value)}
              className="stroke-divider"
            />
            <text
              x={PAD.left - 8}
              y={y(value) + 4}
              textAnchor="end"
              fontSize="11"
              className="fill-footer"
            >
              {value}
            </text>
          </g>
        )
      })}
      <polyline
        points={points
          .map((point, index) => `${String(x(index))},${String(y(point.value))}`)
          .join(' ')}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {points.map((point, index) => (
        <g key={point.label}>
          <circle
            cx={x(index)}
            cy={y(point.value)}
            r="4"
            fill="#fff"
            stroke="currentColor"
            strokeWidth="2"
          >
            <title>{`${point.label}: ${String(point.value)}`}</title>
          </circle>
          <text
            x={x(index)}
            y={HEIGHT - 8}
            textAnchor="middle"
            fontSize="11"
            className="fill-footer"
          >
            {point.label}
          </text>
        </g>
      ))}
    </svg>
  )
}

export default PerformanceMonthChart
